import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';

import { RateCurve } from '@classes/invest/rate-curve';
import { InvestService } from '@services/invest.service';
import { ProviderType } from '@enums/provider-type.enum';
import { InvestRateType } from '@enums/invest-rate-type.enum';
import { IndiceModeType } from '@enums/indice-mode-type.enum';
import { InvestSic } from '@classes/invest/invest-sic/invest-sic';
import { InvestProjectYearSic } from '@classes/invest/invest-sic/invest-project-year-sic';
import { InvestProjectSic } from '@classes/invest/invest-sic/invest-project-sic';


@Component({
    selector: 'app-sic-projects',
    templateUrl: './sic-projects.component.html',
    styleUrls: ['./sic-projects.component.scss']
})
export class SicProjectsComponent implements OnInit, OnDestroy {
    @Input() invest: InvestSic;
    private investSub: Subscription;

    rateCurve: RateCurve[];
    years: number[];
    isLoading: boolean;
    indiceMode = IndiceModeType;

    constructor(
        private investSrv: InvestService
    ) { }

    ngOnInit() {
        this.initSubscriptions();
        this.initDropdowns();
    }

    onValueChanged(value: number, year: InvestProjectYearSic, key: string): void {
        if(key === 'loaningAmount' && value <= 0) {
            year.loaningTypeCode = null;
        }

        if(key === 'rate') {
            year.rate.value = value;
        } else {
            year[key] = value;
        }

        if(key === 'extraFund') {
            this.investSrv.calculateExtraFund(this.invest);
        }

        // year.extraBalance = (year.loaningAmount + year.ordinaryLevy + year.subsidies + year.extraFund) - year.spending;
        year.extraBalance = year.spending - year.loaningAmount - year.ordinaryLevy - year.subsidies - year.estate - year.extraFund;

        this.investSrv.investChanged.next(this.invest);
    }

    checkError(year: InvestProjectYearSic, key?: string): boolean {
        if(year.extraBalance < 0) {
            return true;
        }

        switch(key) {
            case 'extraFund':
                return this.invest.simulation.years.some(y => y.items.find(i => i.code === 'extraFund').value < 0);
            case 'ordinaryLevy':
                return this.testLevyValue(year.year);
            default:
                return false;
        }
    }

    onLoaningTypeChanged(year: InvestProjectYearSic): void {
        if(year.loaningTypeCode && year.year) {
            const selRate = this.rateCurve.find(o => o.code === year.loaningTypeCode);
            const selYearIndex = selRate.years.findIndex(y => y.year === year.year);

            year.rate = {
                type: InvestRateType.selected,
                value: selRate.years[selYearIndex + 1].value // Get selected year + 1
            };
        }
    }

    onProjectsListChanged(projects: InvestProjectSic[]): void {
        this.invest.fundingProject.projects = projects;
        this.investSrv.calculateExtraFund(this.invest);
    }

    onAddYear(project: InvestProjectSic): void {
        project.years.push(new InvestProjectYearSic(null));
    }

    onAddProject(): void {
        this.invest.fundingProject.projects.push(new InvestProjectSic(""));
    }

    onRemoveProject(i: number): void {
        this.invest.fundingProject.projects.splice(i, 1);
        this.investSrv.calculateExtraFund(this.invest);
    }

    onRemoveYear(project: InvestProjectSic, i: number): void {
        project.years.splice(i, 1);

        if(!project.years.length) {
            // create a new blank year
            this.onAddYear(project);
        }
        this.investSrv.calculateExtraFund(this.invest);
    }

    private testLevyValue(testYear: number): boolean {
        const levyTot = this.investSrv.sumProjectsYearProperty(this.invest.fundingProject.projects, testYear, 'ordinaryLevy');
        const simuYear = this.invest.simulation.years.find(y => y.year === testYear);
        return simuYear ? simuYear.items.find(i => i.code === 'balance').value < levyTot : false;
    }

    private initSubscriptions(): void {
        this.investSub = this.investSrv.investChanged
        .subscribe(invest => {
            if(invest && invest.simulation.provider !== ProviderType.bbc) {
                this.invest = <InvestSic>invest;
                this.initDropdowns();
            }
        });
    }

    private initDropdowns(): void {
        if(this.invest) {
            this.rateCurve = this.invest.rateCurve;
            this.years = this.invest.rateCurve[0].years
            .reduce((years, year) => [... years, year.year], []);
        } else {
            this.rateCurve = [];
            this.years = [];
        }
    }

    ngOnDestroy(): void {
        if(this.investSub) {
            this.investSub.unsubscribe();
        }
    }
}
